import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ExternalLink, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { FilterChips, type FilterChip } from '@/components/FilterChips';
import { OpportunityCard as OppCard } from '@/components/cards/OpportunityCard';
import { MultiSelect } from '@/components/MultiSelect';
import { PageHeader } from '@/components/PageHeader';
import { useOpportunities, useOppPicklists, useResources, useCompanyLookup, useMe, useUpdateOpportunity, type OppFilters } from '@/api/hooks';
import { fmtCurrency, fmtDate, stageColor } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';
import type { Opportunity } from '@/api/client';
import { ROUTES, oppPath } from '@/lib/routes';

const AUTOTASK_OPP_URL = 'https://ww31.autotask.net/Mvc/CRM/OpportunityDetail.mvc?opportunityID=';

const EMPTY_FILTERS: OppFilters = { assignedTo: 'any', stage: [], search: '' };

// Autotask returns one page; the server caps at 500. Hitting it means the result is truncated.
const OPP_PAGE_CAP = 500;

type SortKey = 'id' | 'title' | 'amount' | 'probability' | 'projectedCloseDate' | 'lastActivity';
type SortDir = 'asc' | 'desc';

const SELECT_CLS = 'flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const csvNums = (v: string | null) => (v ? v.split(',').map(Number).filter(Number.isFinite) : []);

// URL <-> OppFilters — lets Sales dashboard tiles deep-link a pre-filtered, auto-run search.
function paramsToOppFilters(sp: URLSearchParams): { filters: OppFilters; hasDrill: boolean } {
  const status = csvNums(sp.get('status'));
  const filters: OppFilters = {
    assignedTo: sp.get('assignedTo') === 'me' ? 'me' : 'any',
    stage: csvNums(sp.get('stage')),
    status: status.length ? status : undefined,
    search: sp.get('search') ?? '',
    companyID: sp.get('companyID') ? Number(sp.get('companyID')) : undefined,
    ownerResourceID: sp.get('ownerResourceID') ? Number(sp.get('ownerResourceID')) : undefined,
    createdAfter: sp.get('createdAfter') ?? undefined,
    createdBefore: sp.get('createdBefore') ?? undefined,
    closedAfter: sp.get('closedAfter') ?? undefined,
    closedBefore: sp.get('closedBefore') ?? undefined,
    includeAll: sp.get('includeAll') === '1' || sp.get('includeAll') === 'true' || undefined,
  };
  const hasDrill = [...sp.keys()].some((k) => k !== 'assignedTo');
  return { filters, hasDrill };
}

function oppFiltersToParams(f: OppFilters): URLSearchParams {
  const p = new URLSearchParams();
  p.set('assignedTo', f.assignedTo);
  if (f.stage.length) p.set('stage', f.stage.join(','));
  if (f.status?.length) p.set('status', f.status.join(','));
  if (f.search) p.set('search', f.search);
  if (f.companyID) p.set('companyID', String(f.companyID));
  if (f.ownerResourceID) p.set('ownerResourceID', String(f.ownerResourceID));
  if (f.createdAfter) p.set('createdAfter', f.createdAfter);
  if (f.createdBefore) p.set('createdBefore', f.createdBefore);
  if (f.closedAfter) p.set('closedAfter', f.closedAfter);
  if (f.closedBefore) p.set('closedBefore', f.closedBefore);
  if (f.includeAll) p.set('includeAll', '1');
  return p;
}

export default function OpportunitySearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useMemo(() => paramsToOppFilters(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [text, setText] = useState(initial.hasDrill ? initial.filters.search : '');
  const [company, setCompany] = useState<CompanyOption | null>(null);
  const [ownerResourceID, setOwnerResourceID] = useState(initial.filters.ownerResourceID ? String(initial.filters.ownerResourceID) : '');
  const [stageSel, setStageSel] = useState<number[]>(initial.filters.stage);
  const [statusSel, setStatusSel] = useState<number[]>(initial.filters.status ?? []);
  const [createdAfter, setCreatedAfter] = useState(initial.filters.createdAfter ? initial.filters.createdAfter.slice(0, 10) : '');

  const [submitted, setSubmitted] = useState<OppFilters | null>(initial.hasDrill ? initial.filters : null);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'lastActivity', dir: 'desc' });

  const applyFilters = (next: OppFilters) => {
    setSubmitted(next);
    setSearchParams(oppFiltersToParams(next), { replace: true });
  };

  const picklists = useOppPicklists();
  const resources = useResources();
  const me = useMe();
  const updateOpp = useUpdateOpportunity();
  const opps = useOpportunities(submitted ?? EMPTY_FILTERS, { enabled: submitted !== null, scoped: true });

  const items = opps.data?.items ?? [];
  const myResourceId = me.data?.item?.id;
  const companyIds = useMemo(() => [...new Set(items.map((o) => o.companyID).filter((v): v is number => v != null))], [items]);
  const companies = useCompanyLookup(companyIds);
  const ownerIds = useMemo(() => [...new Set(items.map((o) => o.ownerResourceID).filter((v): v is number => v != null))], [items]);

  const companyMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of companies.data?.items ?? []) m.set(c.id, c.companyName);
    return m;
  }, [companies.data]);

  const resourceMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of resources.data?.items ?? []) m.set(r.id, `${r.firstName} ${r.lastName}`.trim());
    return m;
  }, [resources.data]);

  const pl = picklists.data;
  const stageMap  = useMemo(() => { const m = new Map<string, string>(); for (const o of pl?.stage ?? []) m.set(o.value, o.label); return m; }, [pl]);
  const statusMap = useMemo(() => { const m = new Map<string, string>(); for (const o of pl?.status ?? []) m.set(o.value, o.label); return m; }, [pl]);

  const sortedItems = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      let va: string | number, vb: string | number;
      switch (sort.key) {
        case 'id':               va = a.id;                                                  vb = b.id; break;
        case 'title':            va = (a.title ?? '').toLowerCase();                         vb = (b.title ?? '').toLowerCase(); break;
        case 'amount':           va = a.amount ?? (dir === 1 ? -1 : Infinity);               vb = b.amount ?? (dir === 1 ? -1 : Infinity); break;
        case 'probability':      va = a.probability ?? (dir === 1 ? -1 : Infinity);          vb = b.probability ?? (dir === 1 ? -1 : Infinity); break;
        case 'projectedCloseDate':va= a.projectedCloseDate ? new Date(a.projectedCloseDate).getTime() : (dir === 1 ? Infinity : -1);
                                  vb = b.projectedCloseDate ? new Date(b.projectedCloseDate).getTime() : (dir === 1 ? Infinity : -1); break;
        case 'lastActivity':     va = a.lastActivity ? new Date(a.lastActivity).getTime() : (dir === 1 ? Infinity : -1);
                                 vb = b.lastActivity ? new Date(b.lastActivity).getTime() : (dir === 1 ? Infinity : -1); break;
        default: return 0;
      }
      if (va < vb) return -dir; if (va > vb) return dir; return 0;
    });
  }, [items, sort]);

  const cycleSort = (key: SortKey) =>
    setSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    applyFilters({
      assignedTo: 'any',
      search: text.trim(),
      stage: stageSel,
      // No status picked → search across every status (includeAll). Picking statuses narrows.
      status: statusSel.length ? statusSel : undefined,
      includeAll: statusSel.length === 0,
      companyID: company?.id,
      ownerResourceID: ownerResourceID ? Number(ownerResourceID) : undefined,
      createdAfter: createdAfter ? new Date(createdAfter).toISOString() : undefined,
    });
  };

  const clearSearch = () => {
    setText(''); setCompany(null); setOwnerResourceID('');
    setStageSel([]); setStatusSel([]); setCreatedAfter('');
    setSubmitted(null);
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const chips: FilterChip[] = [];
  if (submitted) {
    const f = submitted;
    const patch = (p: Partial<OppFilters>) => applyFilters({ ...f, ...p });
    if (f.search) chips.push({ key: 'search', label: `“${f.search}”`, onRemove: () => patch({ search: '' }) });
    f.stage.forEach((id) => chips.push({ key: `stage-${id}`, label: `Stage: ${stageMap.get(String(id)) ?? id}`, onRemove: () => patch({ stage: f.stage.filter((x) => x !== id) }) }));
    (f.status ?? []).forEach((id) => chips.push({ key: `status-${id}`, label: `Status: ${statusMap.get(String(id)) ?? id}`, onRemove: () => patch({ status: (f.status ?? []).filter((x) => x !== id) }) }));
    if (f.ownerResourceID) chips.push({ key: 'owner', label: `Owner: ${resourceMap.get(f.ownerResourceID) ?? `#${f.ownerResourceID}`}`, onRemove: () => patch({ ownerResourceID: undefined }) });
    if (f.assignedTo === 'me') chips.push({ key: 'me', label: 'My opportunities', onRemove: () => patch({ assignedTo: 'any' }) });
    if (f.createdAfter) chips.push({ key: 'ca', label: `Created ≥ ${f.createdAfter.slice(0, 10)}`, onRemove: () => patch({ createdAfter: undefined }) });
    if (f.createdBefore) chips.push({ key: 'cb', label: `Created < ${f.createdBefore.slice(0, 10)}`, onRemove: () => patch({ createdBefore: undefined }) });
    if (f.closedAfter) chips.push({ key: 'la', label: `Closed ≥ ${f.closedAfter.slice(0, 10)}`, onRemove: () => patch({ closedAfter: undefined }) });
    if (f.closedBefore) chips.push({ key: 'lb', label: `Closed < ${f.closedBefore.slice(0, 10)}`, onRemove: () => patch({ closedBefore: undefined }) });
    if (f.companyID) chips.push({ key: 'co', label: `Company #${f.companyID}`, onRemove: () => patch({ companyID: undefined }) });
  }

  const SortTh = ({ col, label }: { col: SortKey; label: string }) => (
    <th onClick={() => cycleSort(col)}
      className="cursor-pointer select-none px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground">
      {label}{sort.key === col && <span className="ml-1 opacity-60">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );
  const PlainTh = ({ label }: { label: string }) => (
    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</th>
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4">
      <PageHeader
        title="Opportunity Search"
        isFetching={opps.isFetching}
        subtitle="Search across all opportunities"
        actions={
          <Button size="sm" onClick={() => navigate(ROUTES.psa.opportunities.new)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />New Opportunity
          </Button>
        }
      />

      <form onSubmit={handleSearch} className="mt-4 rounded-lg border border-border bg-card p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Title or description</label>
            <SearchInput value={text} onValueChange={setText} placeholder="Keyword…" autoFocus={!initial.hasDrill} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Company</label>
            <CompanySearch value={company} onChange={setCompany} placeholder="Any company…" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Owner</label>
            <select value={ownerResourceID} onChange={(e) => setOwnerResourceID(e.target.value)} className={SELECT_CLS}>
              <option value="">Any owner</option>
              {(resources.data?.items ?? []).map((r) => (
                <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
            <MultiSelect label="Any status" options={pl?.status ?? []} selected={statusSel} onChange={setStatusSel} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Stage</label>
            <MultiSelect label="Any stage" options={pl?.stage ?? []} selected={stageSel} onChange={setStageSel} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Created after</label>
            <Input type="date" value={createdAfter} onChange={(e) => setCreatedAfter(e.target.value)} />
          </div>
          <div className="ml-auto flex items-end gap-2">
            <Button type="submit">
              <Search className="mr-1.5 h-3.5 w-3.5" />Search
            </Button>
            {submitted && (
              <Button type="button" variant="ghost" size="icon" onClick={clearSearch} aria-label="Clear">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </form>

      <FilterChips chips={chips} onClear={clearSearch} />

      {!submitted && (
        <div className="mt-12 flex flex-col items-center gap-2 text-center text-muted-foreground">
          <Search className="h-8 w-8 opacity-30" />
          <p className="text-sm">Set your filters above and click Search.</p>
        </div>
      )}

      {submitted && opps.isLoading && (
        <div className="mt-8 flex justify-center text-sm text-muted-foreground">Searching…</div>
      )}

      {submitted && !opps.isLoading && items.length === 0 && (
        <div className="mt-8 flex flex-col items-center gap-2 text-center text-muted-foreground">
          <p className="text-sm">No opportunities found. Try adjusting your filters.</p>
        </div>
      )}

      {submitted && items.length > 0 && (
        <>
          <p className="mt-4 text-xs text-muted-foreground">{items.length} result{items.length !== 1 ? 's' : ''}</p>
          {items.length >= OPP_PAGE_CAP && (
            <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-700 dark:text-amber-400">
              Showing the first {OPP_PAGE_CAP} — narrow your filters (status, stage, company, keyword, or created-after) to see the rest.
            </div>
          )}
          {/* Mobile */}
          <div className="mt-2 space-y-2 md:hidden">
            {sortedItems.map((o) => (
              <OppCard
                key={o.id}
                o={o}
                companyMap={companyMap}
                stageMap={stageMap}
                stageOpts={pl?.stage ?? []}
                statusOpts={pl?.status ?? []}
                resourceMap={resourceMap}
                myResourceId={myResourceId}
                onStageChange={(id, stageId) => updateOpp.mutate({ id, fields: { stage: stageId } })}
                onStatusChange={(id, status) => updateOpp.mutate({ id, fields: { status } })}
                pendingStage={updateOpp.isPending && updateOpp.variables?.fields?.stage !== undefined && updateOpp.variables?.id === o.id}
                pendingStatus={updateOpp.isPending && updateOpp.variables?.fields?.status !== undefined && updateOpp.variables?.id === o.id}
              />
            ))}
          </div>

          {/* Desktop */}
          <div className="mt-4 hidden overflow-x-auto rounded-lg border border-border bg-card shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <SortTh col="id" label="#" />
                  <SortTh col="title" label="Title" />
                  <PlainTh label="Company" />
                  <PlainTh label="Stage" />
                  <PlainTh label="Status" />
                  <SortTh col="amount" label="Amount" />
                  <SortTh col="probability" label="Prob %" />
                  <PlainTh label="Owner" />
                  <SortTh col="projectedCloseDate" label="Close" />
                  <SortTh col="lastActivity" label="Last Activity" />
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {sortedItems.map((o) => {
                  const stageLabel = o.stage != null ? stageMap.get(String(o.stage)) ?? null : null;
                  const statusLabel = o.status != null ? statusMap.get(String(o.status)) ?? null : null;
                  const ownerName = o.ownerResourceID != null ? resourceMap.get(o.ownerResourceID) ?? null : null;
                  return (
                    <tr key={o.id} className="group hover:bg-muted/30">
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        <Link to={oppPath(o.id)} className="hover:text-foreground hover:underline">#{o.id}</Link>
                      </td>
                      <td className="max-w-xs px-3 py-2">
                        <Link to={oppPath(o.id)} className="line-clamp-2 text-sm font-medium hover:text-brand">{o.title}</Link>
                      </td>
                      <td className="max-w-[160px] truncate px-3 py-2 text-xs text-muted-foreground">
                        {o.companyID != null ? companyMap.get(o.companyID) ?? '' : ''}
                      </td>
                      <td className="px-3 py-2">
                        {stageLabel && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                            style={{ background: `color-mix(in oklab, ${stageColor(stageLabel)} 15%, transparent)`, color: stageColor(stageLabel) }}>
                            {stageLabel}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {statusLabel ?? '—'}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs tabular-nums">
                        {o.amount != null ? fmtCurrency(o.amount) : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">
                        {o.probability != null ? `${o.probability}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{ownerName ?? '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {o.projectedCloseDate ? fmtDate(o.projectedCloseDate) : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {o.lastActivity ? fmtDate(o.lastActivity) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <a href={`${AUTOTASK_OPP_URL}${o.id}`} target="_blank" rel="noreferrer"
                          className="opacity-0 group-hover:opacity-60 hover:!opacity-100" title="Open in Autotask">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
